import { companyFinder } from './company-finder.ts';
import { browserWallet, qrCanvas, v1Request as v1RequestOf, walletConnect as connectWalletConnect, type Wallet } from './wallet.ts';

/**
 * /onboard/: LEI → GLEIF → one wallet session → verified (SPEC.md §9).
 *
 * Two wallet paths:
 *  - the Concordium MOBILE wallet over WalletConnect v2 (namespace `ccd`,
 *    methods `request_verifiable_presentation_v1` and
 *    `sign_and_send_transaction`): one QR code. That wallet cannot send
 *    RegisterData or a memo over WalletConnect, so the V1 request's anchor is
 *    sent by PageID (when configured) or from a browser wallet, and the
 *    transfer names the session by its exact amount;
 *  - the Concordium BROWSER wallet: anchor, proof and a transfer with the
 *    session code as memo, from the company account itself.
 *
 * The page only carries messages between the wallet and the PageID server;
 * the server verifies everything itself.
 */
type Part = { state: 'ok' | 'fail' | 'pending'; line: string; problems: string[] };
type View = {
  id: string;
  status: string;
  code: string;
  exact_micro: number;
  lei: string;
  gleif: { legalName: string; country: string; city: string | null; registeredAs: string | null };
  domains: string[];
  customer_id: string;
  account: string | null;
  binding_sha256: string | null;
  v1: { context: unknown; subjectClaims: unknown; anchor_hex: string } | null;
  anchor_tx: string | null;
  anchor_state: 'none' | 'seen' | 'final' | 'bad';
  has_proof: boolean;
  transfer: { tx: string; sender: string; amount_micro: number; finalized: boolean; matched_by: string } | null;
  decision: { verified: boolean; proof: Part; transfer: Part; company: { legalName: string; businessNumber: string; legalCountry: string; lei: string; idpName: string } | null } | null;
  errors: string[];
  magic_link?: string | null;
  anchor_by_netid?: boolean;
};
type Config = { network: string; receive_account: string; wc_project_id: string | null; anchor_by_netid: boolean };

const $ = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;

let config: Config;
let view: View | null = null;
let wallet: Wallet | null = null;
let polling = 0;
let proofAsked = false;
let transferAsked = false;
let anchorAsked = false;
/** The guided onboarding's draft (/start/<id>) that sent us here, if any. */
let startId: string | null = null;

async function api<T>(path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, body === undefined ? { credentials: 'same-origin' } : { method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });
  const j = (await res.json().catch(() => ({}))) as T & { error?: string; session?: View };
  if (!res.ok) {
    if (j.session) render(j.session);
    throw new Error(j.error ?? `HTTP ${res.status}`);
  }
  return j;
}

function stepTo(step: 'lei' | 'wallet' | 'done') {
  const order = ['lei', 'wallet', 'done'];
  for (const li of document.querySelectorAll<HTMLElement>('#steps li')) {
    const i = order.indexOf(li.dataset.step!);
    li.className = i < order.indexOf(step) ? 'done' : i === order.indexOf(step) ? 'now' : '';
  }
  $('step-lei').hidden = step !== 'lei';
  $('step-wallet').hidden = step !== 'wallet';
  $('step-done').hidden = step !== 'done';
  document.body.dataset.step = step;
}

// ---- step 1: the LEI ----------------------------------------------------------
$('lei-form').addEventListener('submit', async (e) => {
  e.preventDefault();
  $('lei-error').textContent = '';
  const lei = $<HTMLInputElement>('lei').value.replace(/\s+/g, '').toUpperCase();
  $<HTMLButtonElement>('lei-btn').disabled = true;
  try {
    const r = await api<{ gleif: View['gleif'] & { lei: string; entityStatus: string }; customer: { id: string; domains: string[] } | null }>(`/api/gleif/${encodeURIComponent(lei)}`);
    $('g-name').textContent = r.gleif.legalName;
    $('g-meta').textContent = [r.gleif.country, r.gleif.city, r.gleif.registeredAs && `registered as ${r.gleif.registeredAs}`, `LEI ${r.gleif.lei}`, r.gleif.entityStatus && r.gleif.entityStatus.toLowerCase()].filter(Boolean).join(' · ');
    if (r.customer?.domains.length && !$<HTMLInputElement>('domains').value) $<HTMLInputElement>('domains').value = r.customer.domains.join(', ');
    $('confirm').hidden = false;
    $('lei-form').dataset.lei = lei;
  } catch (err) {
    $('lei-error').textContent = (err as Error).message;
    $('confirm').hidden = true;
    if (leiMode === 'find') {
      $('panel-find').hidden = false;
      $('ob-error').textContent = (err as Error).message;
    }
  } finally {
    $<HTMLButtonElement>('lei-btn').disabled = false;
  }
});
$('no-btn').addEventListener('click', () => {
  $('confirm').hidden = true;
  if (leiMode === 'find') {
    $('panel-find').hidden = false;
    $<HTMLInputElement>('ob-name').focus();
  } else $<HTMLInputElement>('lei').focus();
});

// Step 1 has two ways in: find the company (country, name, optional number: README "Company lookup") or type the LEI.
let leiMode: 'find' | 'lei' = 'find';
function setLeiMode(m: 'find' | 'lei') {
  leiMode = m;
  $('mode-find').setAttribute('aria-pressed', String(m === 'find'));
  $('mode-lei').setAttribute('aria-pressed', String(m === 'lei'));
  $('panel-find').hidden = m !== 'find';
  $('lei-form').hidden = m !== 'lei';
  $('confirm').hidden = true;
}
$('mode-find').addEventListener('click', () => setLeiMode('find'));
$('mode-lei').addEventListener('click', () => {
  setLeiMode('lei');
  $<HTMLInputElement>('lei').focus();
});
$('panel-find').append(
  companyFinder({
    id: 'ob',
    onPick: (c) => {
      // The finder found the LEI at GLEIF: the same confirmation as a typed LEI, with the domains.
      $<HTMLInputElement>('lei').value = c.lei;
      $('panel-find').hidden = true;
      $<HTMLFormElement>('lei-form').requestSubmit();
    },
    onNoLei: () => {
      location.href = startId ? `/start/${startId}#steps` : '/start/';
    },
  }),
);
$('yes-btn').addEventListener('click', async () => {
  $('confirm-error').textContent = '';
  try {
    const v = await api<View>('/api/onboard/start', { lei: $('lei-form').dataset.lei, domains: $<HTMLInputElement>('domains').value });
    sessionStorage.setItem('netid-onboard', v.id);
    render(v);
    stepTo('wallet');
  } catch (err) {
    $('confirm-error').textContent = (err as Error).message;
  }
});

// ---- step 2: the wallet ---------------------------------------------------------
function v1Request(v: View) {
  return v1RequestOf(v.v1!, v.anchor_tx);
}

function walletConnect(): Promise<Wallet> {
  return connectWalletConnect(config.wc_project_id, {
    onUri: showQr,
    onApproved: () => {
      $('wc-qr').hidden = true;
      $('wc-hint').hidden = true;
    },
  });
}

function showQr(uri: string) {
  $('wc-qr').replaceChildren(qrCanvas(uri));
  $('wc-qr').hidden = false;
  $('wc-qr').dataset.uri = uri;
  $('wc-hint').hidden = false;
  $<HTMLAnchorElement>('wc-deeplink').href = `concordiumwallet://wc?uri=${encodeURIComponent(uri)}`;
}

async function useWallet(make: () => Promise<Wallet>) {
  $('wallet-error').textContent = '';
  try {
    wallet = await make();
    render(await api<View>(`/api/onboard/${view!.id}/account`, { account: wallet.account }));
    void drive();
  } catch (e) {
    $('wallet-error').textContent = (e as Error).message;
  }
}
$('wc-btn').addEventListener('click', () => void useWallet(walletConnect));
$('bw-btn').addEventListener('click', () => void useWallet(browserWallet));

/** Moves the session forward: anchor → proof → transfer, polling the server between steps. */
async function drive() {
  if (!view || !wallet) return;
  try {
    if (view.v1 && !view.anchor_tx && !anchorAsked) {
      anchorAsked = true;
      if (wallet.canAnchor && wallet.anchor) render(await api<View>(`/api/onboard/${view.id}/anchor`, { tx: await wallet.anchor(view.v1.anchor_hex) }));
      else if (view.anchor_by_netid) render(await api<View>(`/api/onboard/${view.id}/anchor-by-netid`, {}));
      else anchorHelp();
    }
    if (view.anchor_state === 'final' && !view.has_proof && !proofAsked) {
      proofAsked = true;
      let json: string;
      try {
        json = await wallet.prove(v1Request(view));
      } catch (e) {
        // The wallet found no identity it can prove this with, or the person declined.
        showNoCompany();
        throw e;
      }
      render(await api<View>(`/api/onboard/${view.id}/proof`, { proof: json }));
    }
    if (view.decision?.proof.state === 'ok' && !view.transfer && !transferAsked) {
      transferAsked = true;
      await wallet.pay(config.receive_account, view.exact_micro, view.code);
    }
  } catch (e) {
    $('wallet-error').textContent = (e as Error).message;
  }
  schedule();
}

/** Sends a wallet without a Global FinReg company identity to the guided step "Get a company identity". */
function showNoCompany() {
  $('no-company').hidden = false;
  $<HTMLAnchorElement>('no-company-link').href = startId ? `/start/${startId}?switch=no-company` : '/start/?switch=no-company';
}

function anchorHelp() {
  const box = $('anchor-help');
  box.hidden = false;
  box.replaceChildren(
    document.createTextNode('The mobile wallet answers a proof request only after the request is anchored on chain, and it cannot send that anchor itself. Send it from any account in a Concordium Browser Wallet (≈ 4–5 CCD): '),
  );
  const b = document.createElement('button');
  b.className = 'btn small';
  b.type = 'button';
  b.textContent = 'Anchor with the browser wallet';
  b.addEventListener('click', async () => {
    try {
      const bw = await browserWallet();
      render(await api<View>(`/api/onboard/${view!.id}/anchor`, { tx: await bw.anchor!(view!.v1!.anchor_hex) }));
      box.hidden = true;
      schedule();
    } catch (e) {
      $('wallet-error').textContent = (e as Error).message;
    }
  });
  box.append(b);
}

function schedule() {
  clearTimeout(polling);
  if (!view || view.status === 'verified' || view.status === 'expired') return;
  polling = window.setTimeout(async () => {
    try {
      render(await api<View>(`/api/onboard/${view!.id}`));
    } catch (e) {
      $('wallet-error').textContent = (e as Error).message;
    }
    void drive();
  }, 4000);
}

// ---- rendering --------------------------------------------------------------------
function li(state: 'ok' | 'fail' | 'pending', title: string, line: string, why: string[] = []) {
  const el = document.createElement('li');
  el.className = state;
  el.dataset.state = state;
  const dot = document.createElement('span');
  dot.className = 'dot';
  const body = document.createElement('div');
  const s = document.createElement('strong');
  s.textContent = title;
  const l = document.createElement('div');
  l.className = 'line';
  l.textContent = line;
  body.append(s, l);
  for (const w of why) {
    const d = document.createElement('div');
    d.className = 'why';
    d.textContent = w;
    body.append(d);
  }
  el.append(dot, body);
  return el;
}

function render(v: View) {
  view = v;
  $('code').textContent = v.code;
  $('amount').textContent = `${(v.exact_micro / 1e6).toFixed(6)} CCD`;
  const d = v.decision;
  if (d?.proof.state === 'fail') showNoCompany();
  const items = [
    li(v.account ? 'ok' : 'pending', 'Company account', v.account ? `${v.account.slice(0, 10)}…${v.account.slice(-6)} connected` : 'Connect your wallet: pick the company account (from your Global FinReg company ID).'),
    li(v.anchor_state === 'final' ? 'ok' : v.anchor_state === 'bad' ? 'fail' : 'pending', 'Proof request anchored', v.anchor_state === 'final' ? 'The request is on chain; the wallet can answer it.' : v.anchor_tx ? 'Anchor sent; waiting for finalization…' : 'The wallet only answers anchored requests.'),
    li(d?.proof.state ?? 'pending', 'Company identity proof', d?.proof.line ?? (v.has_proof ? 'Checking the proof…' : 'Approve the proof request in your wallet.'), d?.proof.problems),
    li(d?.transfer.state ?? 'pending', `${(v.exact_micro / 1e6).toFixed(6)} CCD from the company account`, d?.transfer.line ?? 'After the proof: approve the transfer in your wallet.', d?.transfer.problems),
  ];
  $('progress').replaceChildren(...items);
  const tech = $('tech');
  tech.replaceChildren();
  const fact = (k: string, val: string) => {
    const dt = document.createElement('dt');
    dt.textContent = k;
    const dd = document.createElement('dd');
    dd.textContent = val;
    dd.className = 'mono';
    tech.append(dt, dd);
  };
  fact('Session', v.id);
  fact('Session code (memo)', v.code);
  fact('Exact amount', `${v.exact_micro} µCCD`);
  fact('Receiving account', config?.receive_account ?? '');
  if (v.binding_sha256) fact('Binding hash (proof context)', v.binding_sha256);
  if (v.anchor_tx) fact('Anchor', `${v.anchor_tx} (${v.anchor_state})`);
  if (v.transfer) fact('Transfer', `${v.transfer.tx} (${v.transfer.finalized ? 'final' : 'pending'}, by ${v.transfer.matched_by})`);
  for (const e of v.errors) fact('Note', e);
  if (v.status === 'verified') {
    stepTo('done');
    const c = d?.company;
    $('d-name').textContent = c?.legalName ?? '';
    $('d-line').textContent = c ? `${c.legalName} · business number ${c.businessNumber} · ${c.legalCountry} · LEI ${c.lei}, verified by ${c.idpName}. Next: verify your domains and register your binding record on your customer page.` : '';
    if (v.magic_link) {
      const url = new URL(v.magic_link, location.origin).toString();
      $('magic').hidden = false;
      $('magic-link').textContent = url;
      $<HTMLAnchorElement>('magic-open').href = url;
    }
  }
}

$('magic-copy').addEventListener('click', () => {
  const b = $('magic-copy');
  void navigator.clipboard?.writeText($('magic-link').textContent ?? '').then(() => {
    b.textContent = 'Copied';
    b.classList.add('done');
    setTimeout(() => {
      b.textContent = 'Copy';
      b.classList.remove('done');
    }, 1600);
  });
});

// ---- start ----------------------------------------------------------------------------
async function init() {
  config = await api<Config>('/api/config');
  if (!config.wc_project_id) {
    $('wc-setup').hidden = false;
    $('wc-live').hidden = true;
  }
  // From the guided onboarding (/start/): the LEI pre-filled, a way back to the steps.
  const params = new URLSearchParams(location.search);
  const ss = { get: (k: string) => { try { return sessionStorage.getItem(k); } catch { return null; } }, set: (k: string, v: string) => { try { sessionStorage.setItem(k, v); } catch { /* private mode */ } } };
  const fromStart = params.get('start');
  if (fromStart && /^[A-Za-z0-9_-]{24}$/.test(fromStart)) ss.set('pageid-start', fromStart);
  startId = ss.get('pageid-start');
  if (startId) {
    $('back-start').hidden = false;
    $<HTMLAnchorElement>('back-start').href = `/start/${startId}#steps`;
  }
  const leiParam = params.get('lei');
  const sid = sessionStorage.getItem('netid-onboard');
  if (sid) {
    try {
      render(await api<View>(`/api/onboard/${sid}`));
      if (view!.status !== 'expired') {
        stepTo(view!.status === 'verified' ? 'done' : 'wallet');
        schedule();
        return;
      }
    } catch {
      sessionStorage.removeItem('netid-onboard');
    }
  }
  stepTo('lei');
  if (leiParam && /^[0-9A-Za-z]{20}$/.test(leiParam)) {
    setLeiMode('lei');
    $<HTMLInputElement>('lei').value = leiParam.toUpperCase();
    $<HTMLFormElement>('lei-form').requestSubmit();
  }
}
void init();

/** For the design screenshots and tests: render a session view without a wallet. */
(window as unknown as { netidOnboard: unknown }).netidOnboard = {
  render: (v: View) => {
    render(v);
    if (v.status !== 'verified') stepTo('wallet');
  },
  get view() {
    return view;
  },
  showQr,
};
